/**
 * TanStack Query hooks para /v1/membresia — FWA-PWA-032.
 *
 * NO outbox wire — backend cron auto-genera, UI backfill manual es admin-direct online.
 */
import type { Membresia, MembresiaBackfillParams } from '@sisrep/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse, apiGet, apiPost, generateIdempotencyKey } from '../api.js';

export const membresiaKeys = {
  all: ['membresia'] as const,
  list: (filters: MembresiaListFilters) => ['membresia', 'list', filters] as const,
  byQuarter: (quarter: string) => ['membresia', 'quarter', quarter] as const,
};

export interface MembresiaListFilters {
  cursor?: string | undefined;
  limit?: number | undefined;
  quarter_from?: string | undefined;
  quarter_to?: string | undefined;
}

interface MembresiaListResponse {
  data: Membresia[];
  next_cursor: string | null;
  has_more: boolean;
}

function buildListQueryString(filters: MembresiaListFilters): string {
  const params = new URLSearchParams();
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.quarter_from) params.set('quarter_from', filters.quarter_from);
  if (filters.quarter_to) params.set('quarter_to', filters.quarter_to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useMembresiaList(filters: MembresiaListFilters = {}) {
  return useQuery<ApiResponse<MembresiaListResponse>>({
    queryKey: membresiaKeys.list(filters),
    queryFn: () => apiGet<MembresiaListResponse>(`/v1/membresia${buildListQueryString(filters)}`),
  });
}

export function useMembresiaByQuarter(quarter: string | undefined) {
  return useQuery<ApiResponse<Membresia>>({
    queryKey: membresiaKeys.byQuarter(quarter ?? ''),
    queryFn: () => apiGet<Membresia>(`/v1/membresia/${quarter}`),
    enabled: Boolean(quarter),
  });
}

export function useBackfillMembresia() {
  const qc = useQueryClient();
  return useMutation<ApiResponse<Membresia>, Error, MembresiaBackfillParams>({
    mutationKey: ['membresia', 'backfill'],
    mutationFn: (params) =>
      apiPost<Membresia, MembresiaBackfillParams>('/v1/membresia/backfill', params, {
        idempotencyKey: generateIdempotencyKey(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: membresiaKeys.all });
    },
  });
}
